import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  Settings,
  LogOut,
  Shield,
  Bell,
  User,
  Save,
  RefreshCw,
  Activity,
  DollarSign,
  GraduationCap,
  TrendingUp,
  UserPlus,
  Trash2,
  Edit,
  Search,
  Filter,
  Calendar,
  MapPin,
  Plus,
  Eye,
  ClipboardList,
  Timer,
  Zap,
  Target,
  Check,
  X,
  ChevronRight,
  FileText,
  Upload,
  Download,
  File,
  Radio,
  Newspaper
} from "lucide-react";

import AdminPlayerManagement from "../components/AdminPlayerManagement";
import { BlogManager, SocialSlideManager } from "../components/BlogManager";
import { ScoutWireManager } from "../components/ScoutWireManager";
import { TwinSyncManager } from "../components/TwinSyncManager";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NSB Logo Component
import { NSBLogo } from "../components/NSBLogo";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [combines, setCombines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [selectedCombine, setSelectedCombine] = useState(null);
  const [combineRegistrations, setCombineRegistrations] = useState([]);
  const [showCreateCombine, setShowCreateCombine] = useState(false);
  const [showRecordMetrics, setShowRecordMetrics] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const [metrics, setMetrics] = useState({
    college_commits: 127,
    scholarships: 89,
    tuition_saved: "$2.4M",
    combine_tests: 445,
    partner_colleges: 450,
    college_coaches: 6000,
    max_tuition_savings: "$12K",
  });

  const [weights, setWeights] = useState({
    performance: 30,
    competition: 20,
    spi: 20,
    roster_fit: 15,
    nil: 15,
  });

  // New Combine Form State
  const [newCombine, setNewCombine] = useState({
    name: "",
    description: "",
    location: "",
    venue_name: "",
    start_date: "",
    end_date: "",
    registration_deadline: "",
    max_participants: "",
    event_type: "combine",
    age_groups: [],
    positions_tested: [],
    metrics_tested: ["sixty_time", "exit_velocity", "fastball_velocity"],
    registration_fee: "",
    is_public: true,
  });

  // Metrics recording state
  const [metricRecording, setMetricRecording] = useState({
    sixty_time: "",
    exit_velocity: "",
    bat_speed: "",
    fastball_velocity: "",
    infield_velocity: "",
    outfield_velocity: "",
    pop_time: "",
    notes: "",
  });

  // PDF Upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [selectedAthleteForPdf, setSelectedAthleteForPdf] = useState("");
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const userRes = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers,
      });

      if (!userRes.ok) {
        navigate("/login");
        return;
      }

      const userData = await userRes.json();
      setUser(userData);

      // Fetch combines
      const combinesRes = await fetch(`${API_URL}/api/admin/combines`, {
        credentials: "include",
        headers,
      });
      if (combinesRes.ok) {
        const combinesData = await combinesRes.json();
        setCombines(combinesData.events || []);
      }

      // Fetch users
      const usersRes = await fetch(`${API_URL}/api/messaging/users`, {
        credentials: "include",
        headers,
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }

      // Fetch metrics
      const metricsRes = await fetch(`${API_URL}/api/metrics`, { headers });
      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }

      // Fetch evaluation weights
      const weightsRes = await fetch(`${API_URL}/api/admin/evaluation-weights`, {
        credentials: "include",
        headers,
      });
      if (weightsRes.ok) {
        const weightsData = await weightsRes.json();
        if (weightsData.pvi_weights) {
          setWeights({
            performance: weightsData.pvi_weights.performance * 100,
            competition: weightsData.pvi_weights.competition * 100,
            spi: weightsData.pvi_weights.spi * 100,
            roster_fit: weightsData.pvi_weights.roster_fit * 100,
            nil: weightsData.pvi_weights.nil * 100,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleSaveMetrics = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/metrics`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(metrics),
      });

      if (res.ok) {
        toast.success("Metrics updated successfully");
      } else {
        toast.error("Failed to update metrics");
      }
    } catch (error) {
      toast.error("Error saving metrics");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWeights = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/evaluation-weights`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pvi_weights: {
            performance: weights.performance / 100,
            competition: weights.competition / 100,
            spi: weights.spi / 100,
            roster_fit: weights.roster_fit / 100,
            nil: weights.nil / 100,
          },
        }),
      });

      if (res.ok) {
        toast.success("Evaluation weights updated");
      } else {
        toast.error("Failed to update weights");
      }
    } catch (error) {
      toast.error("Error saving weights");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCombine = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/combines`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newCombine,
          max_participants: newCombine.max_participants ? parseInt(newCombine.max_participants) : null,
          registration_fee: newCombine.registration_fee ? parseFloat(newCombine.registration_fee) : null,
        }),
      });

      if (res.ok) {
        toast.success("Combine event created!");
        setShowCreateCombine(false);
        setNewCombine({
          name: "",
          description: "",
          location: "",
          venue_name: "",
          start_date: "",
          end_date: "",
          registration_deadline: "",
          max_participants: "",
          event_type: "combine",
          age_groups: [],
          positions_tested: [],
          metrics_tested: ["sixty_time", "exit_velocity", "fastball_velocity"],
          registration_fee: "",
          is_public: true,
        });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create event");
      }
    } catch (error) {
      toast.error("Error creating event");
    }
  };

  const handleSelectCombine = async (combine) => {
    setSelectedCombine(combine);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/combines/${combine.event_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCombineRegistrations(data.registrations || []);
      }
    } catch (error) {
      console.error("Error fetching combine details:", error);
    }
  };

  const handleRecordMetrics = async () => {
    if (!selectedCombine || !selectedRegistration) return;

    try {
      const token = localStorage.getItem("token");
      const payload = {
        event_id: selectedCombine.event_id,
        athlete_id: selectedRegistration.athlete_id,
        ...Object.fromEntries(
          Object.entries(metricRecording).filter(([k, v]) => v !== "" && k !== "notes").map(([k, v]) => [k, parseFloat(v)])
        ),
        notes: metricRecording.notes,
      };

      const res = await fetch(`${API_URL}/api/combines/${selectedCombine.event_id}/record-metrics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Metrics recorded and athlete profile updated!");
        setShowRecordMetrics(false);
        setMetricRecording({
          sixty_time: "",
          exit_velocity: "",
          bat_speed: "",
          fastball_velocity: "",
          infield_velocity: "",
          outfield_velocity: "",
          pop_time: "",
          notes: "",
        });
        handleSelectCombine(selectedCombine); // Refresh
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to record metrics");
      }
    } catch (error) {
      toast.error("Error recording metrics");
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchTerm || 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-800";
      case "scout": return "bg-blue-100 text-blue-800";
      case "coach": return "bg-green-100 text-green-800";
      case "athlete": return "bg-purple-100 text-purple-800";
      case "agent": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "upcoming": return "bg-blue-100 text-blue-800";
      case "active": return "bg-green-100 text-green-800";
      case "completed": return "bg-gray-100 text-gray-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <NSBLogo className="h-10" light={false} />
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-red-500" />
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Admin Portal</h1>
                  <p className="text-xs text-gray-500">System Management</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-red-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">{user?.name || user?.email}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="combines" data-testid="tab-combines">
              <Calendar className="h-4 w-4 mr-2" />
              Combine Events
            </TabsTrigger>
            <TabsTrigger value="scouting-reports" data-testid="tab-scouting-reports">
              <FileText className="h-4 w-4 mr-2" />
              Scouting Reports
            </TabsTrigger>
            <TabsTrigger value="players" data-testid="tab-players">
              <ClipboardList className="h-4 w-4 mr-2" />
              Players
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">
              <Edit className="h-4 w-4 mr-2" />
              Blog / News
            </TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social">
              <Eye className="h-4 w-4 mr-2" />
              Social Slides
            </TabsTrigger>
            <TabsTrigger value="scout-wire" data-testid="tab-scout-wire">
              <Radio className="h-4 w-4 mr-2" />
              Scout Wire
            </TabsTrigger>
            <TabsTrigger value="twin-sync" data-testid="tab-twin-sync">
              <Newspaper className="h-4 w-4 mr-2" />
              ScoutWire News
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-500">College Commits</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics?.college_commits || 0}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-500">Scholarships</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics?.scholarships || 0}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-900" />
                  </div>
                  <span className="text-sm text-gray-500">Tuition Saved</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics?.tuition_saved || "$0"}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Activity className="h-5 w-5 text-orange-600" />
                  </div>
                  <span className="text-sm text-gray-500">Combine Tests</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics?.combine_tests || 0}</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button variant="outline" onClick={() => setActiveTab("combines")} className="h-auto py-4 flex-col">
                  <Calendar className="h-6 w-6 mb-2 text-blue-500" />
                  <span>Manage Combines</span>
                </Button>
                <Button variant="outline" onClick={() => setActiveTab("users")} className="h-auto py-4 flex-col">
                  <Users className="h-6 w-6 mb-2 text-green-500" />
                  <span>View Users</span>
                </Button>
                <Button variant="outline" onClick={() => setActiveTab("settings")} className="h-auto py-4 flex-col">
                  <Settings className="h-6 w-6 mb-2 text-blue-800" />
                  <span>Update Settings</span>
                </Button>
                <Button variant="outline" onClick={fetchData} className="h-auto py-4 flex-col">
                  <RefreshCw className="h-6 w-6 mb-2 text-orange-500" />
                  <span>Refresh Data</span>
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Combines Tab */}
          <TabsContent value="combines">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Combine List */}
              <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Combine Events</h3>
                  <Button size="sm" onClick={() => setShowCreateCombine(true)}>
                    <Plus className="h-4 w-4 mr-1" /> New
                  </Button>
                </div>
                <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                  {combines.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No combine events yet</p>
                      <Button variant="link" onClick={() => setShowCreateCombine(true)}>
                        Create your first event
                      </Button>
                    </div>
                  ) : (
                    combines.map((combine) => (
                      <button
                        key={combine.event_id}
                        onClick={() => handleSelectCombine(combine)}
                        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                          selectedCombine?.event_id === combine.event_id ? "bg-blue-50 border-l-4 border-blue-500" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{combine.name}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {combine.location}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(combine.start_date).toLocaleDateString()}
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(combine.status)}`}>
                            {combine.status}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Combine Details */}
              <div className="lg:col-span-2">
                {selectedCombine ? (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">{selectedCombine.name}</h2>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {selectedCombine.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(selectedCombine.start_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedCombine.status)}`}>
                          {selectedCombine.status}
                        </span>
                      </div>
                      {selectedCombine.description && (
                        <p className="text-gray-600 mt-4">{selectedCombine.description}</p>
                      )}
                    </div>

                    {/* Registered Athletes */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">
                          Registered Athletes ({combineRegistrations.length})
                        </h3>
                      </div>
                      {combineRegistrations.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>No athletes registered yet</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {combineRegistrations.map((reg) => (
                            <div key={reg.registration_id} className="py-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white font-bold">
                                  {reg.athlete_info?.name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{reg.athlete_info?.name || 'Unknown'}</div>
                                  <div className="text-xs text-gray-500">
                                    {reg.athlete_info?.position_primary} • {reg.athlete_info?.school}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {reg.metrics_recorded ? (
                                  <span className="text-xs text-green-600 flex items-center gap-1">
                                    <Check className="h-3 w-3" /> Recorded
                                  </span>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedRegistration(reg);
                                      setShowRecordMetrics(true);
                                    }}
                                  >
                                    <ClipboardList className="h-4 w-4 mr-1" />
                                    Record Metrics
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                    <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Combine Event</h3>
                    <p className="text-gray-500">Choose an event from the list to view details and manage registrations</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Scouting Reports Tab */}
          <TabsContent value="scouting-reports">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Upload className="h-5 w-5 text-blue-600" />
                  Upload Scouting Report PDF
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Upload an NSB scouting report PDF to automatically extract player data and metrics.
                </p>

                {/* Player Selection */}
                <div className="mb-4">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Select Player (Optional - leave empty to create new)
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search existing players..."
                      value={playerSearchQuery}
                      onChange={async (e) => {
                        setPlayerSearchQuery(e.target.value);
                        if (e.target.value.length >= 2) {
                          try {
                            const token = localStorage.getItem("token");
                            const res = await fetch(`${API_URL}/api/admin/players-for-report?search=${encodeURIComponent(e.target.value)}`, {
                              credentials: "include",
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setPlayerSearchResults(data.players || []);
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        } else {
                          setPlayerSearchResults([]);
                        }
                      }}
                      className="pl-10"
                    />
                  </div>
                  {playerSearchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                      {playerSearchResults.map((p) => (
                        <button
                          key={p.athlete_id}
                          onClick={() => {
                            setSelectedAthleteForPdf(p.athlete_id);
                            setPlayerSearchQuery(p.name);
                            setPlayerSearchResults([]);
                          }}
                          className={`w-full text-left p-3 hover:bg-gray-50 border-b last:border-0 ${selectedAthleteForPdf === p.athlete_id ? 'bg-blue-50' : ''}`}
                        >
                          <span className="font-medium">{p.name}</span>
                          <span className="text-sm text-gray-500 ml-2">{p.school} • {p.position_primary} • {p.graduation_year}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedAthleteForPdf && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-green-600">Selected: {playerSearchQuery}</span>
                      <button 
                        onClick={() => {
                          setSelectedAthleteForPdf("");
                          setPlayerSearchQuery("");
                        }}
                        className="text-red-500 text-sm hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    PDF File
                  </Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setPdfFile(e.target.files[0]);
                          setUploadResult(null);
                        }
                      }}
                      className="hidden"
                      id="pdf-upload"
                    />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      {pdfFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <File className="h-8 w-8 text-red-500" />
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{pdfFile.name}</p>
                            <p className="text-sm text-gray-500">{(pdfFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600">Click to upload or drag and drop</p>
                          <p className="text-sm text-gray-400">PDF files only</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Upload Button */}
                <Button
                  onClick={async () => {
                    if (!pdfFile) {
                      toast.error("Please select a PDF file");
                      return;
                    }
                    setPdfUploading(true);
                    setUploadResult(null);
                    try {
                      const token = localStorage.getItem("token");
                      const formData = new FormData();
                      formData.append("file", pdfFile);
                      if (selectedAthleteForPdf) {
                        formData.append("athlete_id", selectedAthleteForPdf);
                      }
                      
                      const res = await fetch(`${API_URL}/api/admin/upload-scouting-report`, {
                        method: "POST",
                        credentials: "include",
                        headers: { Authorization: `Bearer ${token}` },
                        body: formData
                      });
                      
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(data.message || "Scouting report uploaded!");
                        setUploadResult(data);
                        setPdfFile(null);
                        setSelectedAthleteForPdf("");
                        setPlayerSearchQuery("");
                      } else {
                        toast.error(data.detail || "Upload failed");
                      }
                    } catch (err) {
                      toast.error("Upload failed: " + err.message);
                    } finally {
                      setPdfUploading(false);
                    }
                  }}
                  disabled={!pdfFile || pdfUploading}
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                >
                  {pdfUploading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload & Extract Data
                    </>
                  )}
                </Button>
              </div>

              {/* Results Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-green-600" />
                  Extracted Data
                </h3>
                
                {uploadResult ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 font-medium flex items-center gap-2">
                        <Check className="h-5 w-5" />
                        {uploadResult.message}
                      </p>
                      {uploadResult.athlete_id && (
                        <p className="text-sm text-green-600 mt-1">
                          Athlete ID: {uploadResult.athlete_id}
                        </p>
                      )}
                    </div>

                    {uploadResult.extracted_data && (
                      <div className="space-y-3">
                        {uploadResult.extracted_data.player_name && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Player Name</span>
                            <span className="font-medium">{uploadResult.extracted_data.player_name}</span>
                          </div>
                        )}
                        {uploadResult.extracted_data.team && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Team</span>
                            <span className="font-medium">{uploadResult.extracted_data.team}</span>
                          </div>
                        )}
                        {uploadResult.extracted_data.position && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Position</span>
                            <span className="font-medium">{uploadResult.extracted_data.position}</span>
                          </div>
                        )}
                        {uploadResult.extracted_data.class_year && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-500">Class Year</span>
                            <span className="font-medium">{uploadResult.extracted_data.class_year}</span>
                          </div>
                        )}
                        
                        {/* Metrics */}
                        {uploadResult.extracted_data.metrics && Object.keys(uploadResult.extracted_data.metrics).length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-semibold text-gray-700 mb-2">Extracted Metrics</h4>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(uploadResult.extracted_data.metrics).map(([key, value]) => (
                                <div key={key} className="bg-gray-50 p-2 rounded">
                                  <span className="text-xs text-gray-500 block">{key.replace(/_/g, " ").toUpperCase()}</span>
                                  <span className="font-bold text-gray-900">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* View Profile Button */}
                        {uploadResult.athlete_id && (
                          <Link to={`/athlete/${uploadResult.athlete_id}`}>
                            <Button variant="outline" className="w-full mt-4">
                              <Eye className="h-4 w-4 mr-2" />
                              View Player Profile
                            </Button>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p>Upload a scouting report PDF to see extracted data here</p>
                    <p className="text-sm text-gray-400 mt-2">
                      The system will automatically extract player info, metrics, and scouting notes
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Players Management Tab */}
          <TabsContent value="players">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">Player Management</h2>
                <p className="text-sm text-gray-500 mt-1">
                  View all players, link scouting reports to user accounts, and manage profiles.
                </p>
              </div>
              <AdminPlayerManagement />
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="athlete">Athlete</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="scout">Scout</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="divide-y divide-gray-100">
                {filteredUsers.map((u) => (
                  <div key={u.user_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white font-bold">
                        {u.name?.charAt(0) || u.email?.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{u.name || 'Unnamed'}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(u.role)}`}>
                      {u.role}
                    </span>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-200 text-sm text-gray-500">
                Showing {filteredUsers.length} of {users.length} users
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Dashboard Metrics */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Dashboard Metrics</h3>
                <div className="space-y-4">
                  <div>
                    <Label>College Commits</Label>
                    <Input
                      type="number"
                      value={metrics.college_commits || ""}
                      onChange={(e) => setMetrics({ ...metrics, college_commits: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Scholarships</Label>
                    <Input
                      type="number"
                      value={metrics.scholarships || ""}
                      onChange={(e) => setMetrics({ ...metrics, scholarships: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Tuition Saved</Label>
                    <Input
                      value={metrics.tuition_saved || ""}
                      onChange={(e) => setMetrics({ ...metrics, tuition_saved: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Combine Tests</Label>
                    <Input
                      type="number"
                      value={metrics.combine_tests || ""}
                      onChange={(e) => setMetrics({ ...metrics, combine_tests: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <Button onClick={handleSaveMetrics} disabled={saving} className="w-full">
                    <Save className="h-4 w-4 mr-2" />
                    Save Metrics
                  </Button>
                </div>
              </div>

              {/* PVI Weights */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">PVI Evaluation Weights</h3>
                <p className="text-sm text-gray-500 mb-4">Weights must total 100%</p>
                <div className="space-y-4">
                  <div>
                    <Label>Performance Weight (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={weights.performance}
                      onChange={(e) => setWeights({ ...weights, performance: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Competition Weight (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={weights.competition}
                      onChange={(e) => setWeights({ ...weights, competition: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>SPI Weight (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={weights.spi}
                      onChange={(e) => setWeights({ ...weights, spi: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Roster Fit Weight (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={weights.roster_fit}
                      onChange={(e) => setWeights({ ...weights, roster_fit: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>NIL Weight (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={weights.nil}
                      onChange={(e) => setWeights({ ...weights, nil: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    Total: {weights.performance + weights.competition + weights.spi + weights.roster_fit + weights.nil}%
                  </div>
                  <Button onClick={handleSaveWeights} disabled={saving} className="w-full">
                    <Save className="h-4 w-4 mr-2" />
                    Save Weights
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <BlogManager />
          </TabsContent>

          {/* Social Slides Tab */}
          <TabsContent value="social">
            <SocialSlideManager />
          </TabsContent>
          <TabsContent value="scout-wire">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <ScoutWireManager />
            </div>
          </TabsContent>
          <TabsContent value="twin-sync">
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-6">
              <TwinSyncManager />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Combine Modal */}
      {showCreateCombine && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold text-gray-900">Create Combine Event</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateCombine(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label>Event Name *</Label>
                <Input
                  placeholder="e.g., Dallas Regional Combine"
                  value={newCombine.name}
                  onChange={(e) => setNewCombine({ ...newCombine, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm"
                  rows={3}
                  placeholder="Event description..."
                  value={newCombine.description}
                  onChange={(e) => setNewCombine({ ...newCombine, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Location *</Label>
                  <Input
                    placeholder="Dallas, TX"
                    value={newCombine.location}
                    onChange={(e) => setNewCombine({ ...newCombine, location: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Venue Name</Label>
                  <Input
                    placeholder="Grand Slam Sports Complex"
                    value={newCombine.venue_name}
                    onChange={(e) => setNewCombine({ ...newCombine, venue_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={newCombine.start_date}
                    onChange={(e) => setNewCombine({ ...newCombine, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={newCombine.end_date}
                    onChange={(e) => setNewCombine({ ...newCombine, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type</Label>
                  <Select value={newCombine.event_type} onValueChange={(v) => setNewCombine({ ...newCombine, event_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="combine">Combine</SelectItem>
                      <SelectItem value="showcase">Showcase</SelectItem>
                      <SelectItem value="tryout">Tryout</SelectItem>
                      <SelectItem value="camp">Camp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max Participants</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={newCombine.max_participants}
                    onChange={(e) => setNewCombine({ ...newCombine, max_participants: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Registration Fee ($)</Label>
                <Input
                  type="number"
                  placeholder="75.00"
                  value={newCombine.registration_fee}
                  onChange={(e) => setNewCombine({ ...newCombine, registration_fee: e.target.value })}
                />
              </div>
              <Button onClick={handleCreateCombine} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Create Event
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Record Metrics Modal */}
      {showRecordMetrics && selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h3 className="font-semibold text-gray-900">Record Metrics</h3>
                <p className="text-sm text-gray-500">{selectedRegistration.athlete_info?.name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowRecordMetrics(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Timer className="h-3 w-3" /> 60-Yard Dash (sec)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="6.8"
                    value={metricRecording.sixty_time}
                    onChange={(e) => setMetricRecording({ ...metricRecording, sixty_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Zap className="h-3 w-3" /> Exit Velocity (mph)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="89"
                    value={metricRecording.exit_velocity}
                    onChange={(e) => setMetricRecording({ ...metricRecording, exit_velocity: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bat Speed (mph)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="68"
                    value={metricRecording.bat_speed}
                    onChange={(e) => setMetricRecording({ ...metricRecording, bat_speed: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Target className="h-3 w-3" /> Fastball Velo (mph)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="85"
                    value={metricRecording.fastball_velocity}
                    onChange={(e) => setMetricRecording({ ...metricRecording, fastball_velocity: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Infield Velo (mph)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="82"
                    value={metricRecording.infield_velocity}
                    onChange={(e) => setMetricRecording({ ...metricRecording, infield_velocity: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Outfield Velo (mph)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="88"
                    value={metricRecording.outfield_velocity}
                    onChange={(e) => setMetricRecording({ ...metricRecording, outfield_velocity: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Pop Time (sec)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="2.0"
                  value={metricRecording.pop_time}
                  onChange={(e) => setMetricRecording({ ...metricRecording, pop_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm"
                  rows={2}
                  placeholder="Additional observations..."
                  value={metricRecording.notes}
                  onChange={(e) => setMetricRecording({ ...metricRecording, notes: e.target.value })}
                />
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <strong>Note:</strong> Recording metrics will automatically update the athlete's public profile and populate the leaderboards.
              </div>
              <Button onClick={handleRecordMetrics} className="w-full">
                <Save className="h-4 w-4 mr-2" />
                Save Metrics
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
